import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Receipt, ArrowClockwise, DownloadSimple, CircleNotch, CheckCircle, Clock, XCircle } from "@phosphor-icons/react";
import api from "../lib/api";

const STATUS_META = {
  credited: { icon: CheckCircle, color: "text-emerald-700", bg: "bg-emerald-50", label: "Paid & credited" },
  created: { icon: Clock, color: "text-amber-700", bg: "bg-amber-50", label: "Awaiting payment" },
  verification_failed: { icon: XCircle, color: "text-rose-700", bg: "bg-rose-50", label: "Verification failed" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) +
      " · " + d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function OrderHistory() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/wallet/orders");
      setOrders(data.orders || []);
    } catch {
      toast.error("Couldn't load orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const downloadInvoice = async (order_id) => {
    setDownloading(order_id);
    try {
      const resp = await api.get(`/wallet/orders/${order_id}/invoice`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `AstroKuber-Invoice-${order_id.slice(-12)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invoice download failed");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="card-surface rounded-md p-7" data-testid="order-history">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-full bg-[#FFF6E0] text-gold">
            <Receipt size={18} weight="duotone" />
          </div>
          <div>
            <p className="label-eyebrow">Order history</p>
            <h3 className="font-serif-display text-2xl font-light mt-1">Top-ups &amp; invoices</h3>
          </div>
        </div>
        <button onClick={refresh} className="btn-ghost rounded-full px-3 py-1.5 text-xs flex items-center gap-1" data-testid="orders-refresh">
          <ArrowClockwise size={12} /> Refresh
        </button>
      </div>

      <div className="mt-5">
        {loading ? (
          <div className="text-muted text-sm flex items-center gap-2 py-6 justify-center">
            <CircleNotch size={14} className="animate-spin" /> Loading orders...
          </div>
        ) : orders.length === 0 ? (
          <div className="text-muted text-sm py-6 text-center font-light">
            No top-ups yet. Add credits to start chatting with our astrologers.
          </div>
        ) : (
          <div className="divide-y divide-soft/40">
            {orders.map((o) => {
              const meta = STATUS_META[o.status] || STATUS_META.created;
              const Icon = meta.icon;
              return (
                <div key={o.order_id} className="py-4 flex items-center gap-4 flex-wrap" data-testid={`order-${o.order_id}`}>
                  <div className={`shrink-0 p-2 rounded-full ${meta.bg} ${meta.color}`}>
                    <Icon size={16} weight="fill" />
                  </div>
                  <div className="flex-1 min-w-[180px]">
                    <div className="text-sm font-medium text-[#3F2D1B]">
                      ₹{o.amount.toFixed(2)}
                      {o.bonus > 0 && (
                        <span className="ml-2 text-[10px] text-emerald-700 font-semibold uppercase tracking-wider">+ ₹{o.bonus} bonus</span>
                      )}
                    </div>
                    <div className="text-xs text-muted mt-0.5">{fmtDate(o.verified_at || o.created_at)}</div>
                    <div className="text-[10px] text-muted/80 mt-0.5 font-mono break-all">{o.order_id}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-[10px] uppercase tracking-wider font-semibold ${meta.color}`}>{meta.label}</span>
                    {o.status === "credited" && (
                      <button
                        onClick={() => downloadInvoice(o.order_id)}
                        disabled={downloading === o.order_id}
                        className="btn-ghost rounded-full px-3 py-1.5 text-xs flex items-center gap-1 disabled:opacity-50"
                        data-testid={`invoice-${o.order_id}`}
                      >
                        {downloading === o.order_id ? (
                          <CircleNotch size={12} className="animate-spin" />
                        ) : (
                          <DownloadSimple size={12} weight="bold" />
                        )}
                        Invoice
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
